import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Home, Search, Package, ShoppingBag, Filter, Gamepad2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const CATEGORIES = ["all", "game", "console", "accessory"] as const;
const PLATFORMS = ["All Platforms", "Nintendo 64", "Game Boy", "Game Boy Advance", "GameCube", "PlayStation", "PlayStation 2", "Sega Genesis"] as const;

const Shop = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [platform, setPlatform] = useState<string>("All Platforms");

  const { data: products, isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("active", true)
        .order("featured", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const filtered = products?.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.description?.toLowerCase().includes(search.toLowerCase());
    const matchCategory = category === "all" || p.category === category;
    const matchPlatform = platform === "All Platforms" || p.platform === platform;
    return matchSearch && matchCategory && matchPlatform;
  });

  return (
    <div className="min-h-screen bg-background relative">
      <div className="fixed inset-0 pointer-events-none scanline opacity-20 z-50" />

      <div className="relative z-10 max-w-lg mx-auto px-4 py-6 space-y-5">
        {/* Header */}
        <header className="space-y-1">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-primary transition-colors mb-2"
          >
            <Home className="w-4 h-4" />
            HOME
          </button>
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold font-mono tracking-tight text-foreground">
              SHOP <span className="text-primary">INVENTORY</span>
            </h1>
          </div>
          <p className="text-xs font-mono text-muted-foreground tracking-wider">
            RETRO GAMES • CONSOLES • ACCESSORIES
          </p>
          <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
        </header>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 font-mono text-sm bg-card border-border focus:border-primary"
          />
        </div>

        {/* Category filters */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 px-1">
            <Filter className="w-3 h-3 text-primary/60" />
            <span className="text-[10px] font-mono text-primary/60 tracking-[0.2em] font-bold">CATEGORY</span>
            <div className="flex-1 h-px bg-border" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-1.5 rounded-md text-xs font-mono tracking-wider border transition-all duration-300 ${
                  category === cat
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-foreground border-border hover:border-primary/60"
                }`}
              >
                {cat.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Platform filter */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 px-1">
            <Gamepad2 className="w-3 h-3 text-primary/60" />
            <span className="text-[10px] font-mono text-primary/60 tracking-[0.2em] font-bold">PLATFORM</span>
            <div className="flex-1 h-px bg-border" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {PLATFORMS.map((plat) => (
              <button
                key={plat}
                onClick={() => setPlatform(plat)}
                className={`px-3 py-1.5 rounded-md text-[10px] font-mono tracking-wider border transition-all duration-300 ${
                  platform === plat
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-foreground border-border hover:border-primary/60"
                }`}
              >
                {plat.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <div className="text-xs font-mono text-muted-foreground">
          {isLoading ? "Loading..." : `${filtered?.length ?? 0} items found`}
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-2 gap-3">
          {filtered?.map((product) => (
            <div
              key={product.id}
              className="group rounded-lg border border-border bg-card overflow-hidden hover:border-primary/60 transition-all duration-300"
            >
              {/* Image placeholder */}
              <div className="aspect-square bg-muted/30 flex items-center justify-center relative">
                {product.image_url ? (
                  <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <Package className="w-10 h-10 text-muted-foreground/30" />
                )}
                {product.featured && (
                  <Badge className="absolute top-1.5 left-1.5 bg-primary text-primary-foreground text-[8px] font-mono px-1.5 py-0.5">
                    FEATURED
                  </Badge>
                )}
                {product.inventory_count <= 2 && product.inventory_count > 0 && (
                  <Badge variant="destructive" className="absolute top-1.5 right-1.5 text-[8px] font-mono px-1.5 py-0.5">
                    LOW STOCK
                  </Badge>
                )}
                {product.inventory_count === 0 && (
                  <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
                    <span className="font-mono text-xs text-destructive font-bold tracking-wider">SOLD OUT</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-2.5 space-y-1">
                <p className="font-mono text-[11px] font-semibold text-foreground leading-tight line-clamp-2">
                  {product.name}
                </p>
                <p className="font-mono text-[9px] text-muted-foreground line-clamp-1">
                  {product.platform} • {product.condition?.toUpperCase()}
                </p>
                <div className="flex items-center justify-between pt-1">
                  <span className="font-mono text-sm font-bold text-primary">
                    ${Number(product.price).toFixed(2)}
                  </span>
                  <span className="font-mono text-[9px] text-muted-foreground">
                    QTY: {product.inventory_count}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {!isLoading && filtered?.length === 0 && (
          <div className="text-center py-12 space-y-2">
            <Package className="w-10 h-10 text-muted-foreground/30 mx-auto" />
            <p className="font-mono text-sm text-muted-foreground">No items found</p>
          </div>
        )}

        {/* Footer */}
        <footer className="text-center pt-4 pb-8">
          <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent mb-2" />
          <p className="text-[10px] font-mono text-muted-foreground/50">
            PRICES & AVAILABILITY SUBJECT TO CHANGE
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Shop;
